import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Copy, X, Mail, CircleCheck, CircleAlert } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/field';
import { useBrief, BRIEF_QUERY_KEY } from '@/lib/brief';
import { env } from '@/lib/env';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/brief/testimonials')({
  staticData: { crumb: 'Opinie' },
  component: TestimonialsPage,
});

interface Testimonial {
  id: string;
  attribution: string;
  quote: string;
  service_slug?: string;
  date?: string;
  subject_email?: string;
  consent_signed_at?: string;
  consent_hash?: string;
}

interface UpsertInput {
  id?: string;
  attribution: string;
  quote: string;
  service_slug?: string;
  date?: string;
  subject_email?: string;
}

function useUpsertTestimonial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertInput): Promise<{ id: string }> => {
      const res = await fetch(`${env.apiBaseUrl}/api/admin/testimonials`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'save_failed');
      }
      return (await res.json()) as { id: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BRIEF_QUERY_KEY }),
  });
}

function useDeleteTestimonial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/admin/testimonials/${encodeURIComponent(id)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!res.ok) throw new Error('delete_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BRIEF_QUERY_KEY }),
  });
}

function useConsentLink() {
  return useMutation({
    mutationFn: async (id: string): Promise<{ url: string; expires_at: number }> => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/admin/testimonials/${encodeURIComponent(id)}/consent-link`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) throw new Error('link_failed');
      return (await res.json()) as { url: string; expires_at: number };
    },
  });
}

function TestimonialsPage() {
  const { data: brief } = useBrief();
  const upsert = useUpsertTestimonial();
  const remove = useDeleteTestimonial();
  const list = ((brief?.testimonials as Testimonial[] | undefined) ?? []) as Testimonial[];

  const [editing, setEditing] = React.useState<Testimonial | 'new' | null>(null);
  const [feedback, setFeedback] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null
  );

  const onSave = async (data: UpsertInput) => {
    setFeedback(null);
    try {
      await upsert.mutateAsync(data);
      setEditing(null);
      setFeedback({ kind: 'ok', text: 'Zapisano opinię.' });
    } catch (e) {
      setFeedback({ kind: 'err', text: (e as Error).message });
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">Opinie pacjentów</h1>
        <p className="mt-1 text-sm text-text-muted">
          Każda opinia wymaga jednorazowej zgody pacjenta. Generuj 7-dniowy link
          zgody (RODO art. 6 ust. 1 lit. a) i wyślij właścicielowi — po jego akceptacji
          opinia pojawi się na stronie po następnej publikacji.
        </p>
      </header>

      {feedback ? (
        <p
          role="status"
          className={cn(
            'rounded-md px-4 py-2 text-sm ring-1',
            feedback.kind === 'ok'
              ? 'bg-success/10 text-success ring-success/20'
              : 'bg-error/10 text-error ring-error/20'
          )}
        >
          {feedback.text}
        </p>
      ) : null}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Lista opinii</CardTitle>
            <CardDescription>
              {list.length} {list.length === 1 ? 'opinia' : 'opinii'} ·{' '}
              {list.filter((t) => t.consent_signed_at).length} z podpisaną zgodą.
            </CardDescription>
          </div>
          <Button type="button" onClick={() => setEditing('new')}>
            <Plus className="size-4" aria-hidden="true" /> Dodaj opinię
          </Button>
        </CardHeader>
        <CardContent>
          {list.length === 0 ? (
            <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
              Brak opinii. Dodaj pierwszą — wprowadź treść + atrybucję, potem wyślij
              właścicielowi link zgody.
            </p>
          ) : (
            <ul role="list" className="space-y-3">
              {list.map((t) => (
                <TestimonialRow
                  key={t.id}
                  item={t}
                  onEdit={() => setEditing(t)}
                  onDelete={() => {
                    if (confirm('Usunąć tę opinię? Ta operacja jest nieodwracalna.')) {
                      remove.mutate(t.id);
                    }
                  }}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {editing ? (
        <TestimonialEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
          busy={upsert.isPending}
        />
      ) : null}
    </div>
  );
}

function TestimonialRow({
  item,
  onEdit,
  onDelete,
}: {
  item: Testimonial;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const link = useConsentLink();
  const [linkInfo, setLinkInfo] = React.useState<{ url: string; expires_at: number } | null>(null);
  const [copied, setCopied] = React.useState(false);
  const signed = !!item.consent_signed_at;

  const onIssue = async () => {
    const r = await link.mutateAsync(item.id);
    setLinkInfo(r);
  };

  const onCopy = async () => {
    if (!linkInfo) return;
    try {
      await navigator.clipboard.writeText(linkInfo.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  };

  return (
    <li className="rounded-md bg-surface p-4 ring-1 ring-black/5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-baseline gap-2">
            <span className="font-display text-sm font-semibold text-text">{item.attribution}</span>
            {item.service_slug ? (
              <span className="rounded bg-bg px-1.5 py-0.5 text-[10px] font-mono text-text-subtle ring-1 ring-border">
                {item.service_slug}
              </span>
            ) : null}
            {item.date ? <span className="text-xs text-text-subtle">{item.date}</span> : null}
            <ConsentBadge signed={signed} signedAt={item.consent_signed_at} />
          </p>
          <p className="mt-2 line-clamp-2 text-sm italic text-text-muted">„{item.quote}"</p>
          {item.subject_email ? (
            <p className="mt-1 inline-flex items-center gap-1 text-xs text-text-subtle">
              <Mail className="size-3" aria-hidden="true" /> {item.subject_email}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <Button type="button" size="sm" variant="ghost" onClick={onEdit}>
            Edytuj
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label="Usuń opinię"
            onClick={onDelete}
          >
            <Trash2 className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {!signed ? (
        <div className="mt-3 border-t border-border pt-3">
          {linkInfo ? (
            <div className="space-y-2">
              <p className="text-xs text-text-muted">
                Link wygasa{' '}
                {new Date(linkInfo.expires_at * 1000).toLocaleString('pl-PL')}. Wyślij go
                pacjentowi e-mailem; po kliknięciu „Wyrażam zgodę" status powyżej zmieni
                się na „Podpisana".
              </p>
              <div className="flex flex-wrap gap-2">
                <Input readOnly value={linkInfo.url} onFocus={(e) => e.currentTarget.select()} />
                <Button type="button" variant="secondary" size="sm" onClick={onCopy}>
                  <Copy className="size-3.5" aria-hidden="true" />
                  {copied ? 'Skopiowano' : 'Kopiuj'}
                </Button>
                {item.subject_email ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const subject = encodeURIComponent('Prośba o zgodę na publikację opinii');
                      const body = encodeURIComponent(
                        `Dzień dobry,\n\nProsimy o jednorazową zgodę na publikację Państwa opinii.\nLink: ${linkInfo.url}\n\nDziękujemy!`
                      );
                      window.location.href = `mailto:${item.subject_email}?subject=${subject}&body=${body}`;
                    }}
                  >
                    <Mail className="size-3.5" aria-hidden="true" /> Wyślij e-mail
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={onIssue}
              disabled={link.isPending}
            >
              Wygeneruj link zgody (7 dni)
            </Button>
          )}
        </div>
      ) : null}
    </li>
  );
}

function ConsentBadge({ signed, signedAt }: { signed: boolean; signedAt?: string }) {
  if (signed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[10px] text-success ring-1 ring-success/20">
        <CircleCheck className="size-3" aria-hidden="true" />
        Zgoda podpisana{signedAt ? ` ${new Date(signedAt).toLocaleDateString('pl-PL')}` : ''}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-[10px] text-warning ring-1 ring-warning/20">
      <CircleAlert className="size-3" aria-hidden="true" />
      Brak zgody — nie publikujemy
    </span>
  );
}

function TestimonialEditor({
  initial,
  onClose,
  onSave,
  busy,
}: {
  initial: Testimonial | null;
  onClose: () => void;
  onSave: (data: UpsertInput) => void;
  busy: boolean;
}) {
  const [attribution, setAttribution] = React.useState(initial?.attribution ?? '');
  const [quote, setQuote] = React.useState(initial?.quote ?? '');
  const [serviceSlug, setServiceSlug] = React.useState(initial?.service_slug ?? '');
  const [date, setDate] = React.useState(initial?.date ?? '');
  const [subjectEmail, setSubjectEmail] = React.useState(initial?.subject_email ?? '');
  const [err, setErr] = React.useState<string | null>(null);

  const submit = () => {
    setErr(null);
    if (attribution.length < 3) {
      setErr('Atrybucja: min. 3 znaki.');
      return;
    }
    if (quote.length < 40) {
      setErr('Cytat: min. 40 znaków.');
      return;
    }
    onSave({
      id: initial?.id,
      attribution,
      quote,
      service_slug: serviceSlug || undefined,
      date: date || undefined,
      subject_email: subjectEmail || undefined,
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center bg-text/40 p-4"
    >
      <div className="relative w-full max-w-2xl space-y-4 rounded-[var(--radius-card)] bg-bg p-6 shadow-xl ring-1 ring-black/10">
        <button
          type="button"
          className="absolute right-4 top-4 rounded-md p-1 text-text-muted hover:text-text"
          onClick={onClose}
          aria-label="Zamknij"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
        <h2 className="font-display text-lg font-semibold text-text">
          {initial ? 'Edytuj opinię' : 'Nowa opinia'}
        </h2>

        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Atrybucja" required htmlFor="attr">
            <Input
              id="attr"
              placeholder="Anna K., właścicielka Maxa"
              value={attribution}
              onChange={(e) => setAttribution(e.target.value)}
            />
          </Field>
          <Field label="E-mail osoby cytowanej" hint="do wysyłki linku zgody" htmlFor="email">
            <Input
              id="email"
              type="email"
              placeholder="anna@example.com"
              value={subjectEmail}
              onChange={(e) => setSubjectEmail(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Cytat" hint="40-1200 znaków" required htmlFor="quote">
          <Textarea
            id="quote"
            rows={5}
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            placeholder="Po wizycie w Państwa klinice…"
          />
        </Field>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Powiązana usługa (slug)" htmlFor="service">
            <Input
              id="service"
              placeholder="sterylizacja-suczki"
              value={serviceSlug}
              onChange={(e) => setServiceSlug(e.target.value)}
            />
          </Field>
          <Field label="Data wizyty" hint="np. marzec 2026" htmlFor="date">
            <Input
              id="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              placeholder="marzec 2026"
            />
          </Field>
        </div>

        {err ? (
          <p className="rounded-md bg-error/10 px-3 py-2 text-sm text-error ring-1 ring-error/20">{err}</p>
        ) : null}

        <p className="rounded-md bg-warning/10 p-3 text-xs text-warning ring-1 ring-warning/20">
          Po zapisaniu opinii musisz wygenerować link zgody i wysłać go osobie cytowanej.
          Bez podpisanej zgody opinia <strong>nie pojawi się</strong> na stronie kliniki.
        </p>

        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="button" onClick={submit} disabled={busy}>
            {initial ? 'Zapisz zmiany' : 'Dodaj opinię'}
          </Button>
        </div>
      </div>
    </div>
  );
}
